import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Banner,
  Breadcrumbs,
  Button,
  Empty,
  LayerCard,
  Loader,
  Select,
  Table,
} from '@cloudflare/kumo'
import {
  ClockCounterClockwiseIcon,
  DoorOpenIcon,
  HouseIcon,
  QuestionIcon,
} from '@phosphor-icons/react'
import { ApiError } from '../api/client'
import {
  getActuation,
  getLastSeen,
  getUnknownBadgers,
  listActuatableReaders,
  openDoor,
  type ActorIdentityMap,
  type LastSeenEntry,
  type UnknownBadger,
} from '../api/access'
import { useHasRole } from '../auth/useRoles'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'

// Task 5's poll target/window — see the "no confirmation" state below.
const DOOR_POLL_INTERVAL_MS = 1000
const DOOR_POLL_WINDOW_MS = 10_000

type DoorState = 'idle' | 'opening' | 'opened' | 'unconfirmed'

type AccessTab = 'lastSeen' | 'unknown'

const DEFAULT_WINDOW_HOURS = 12

// Sentinel written by deviceMonitor.ts (apps/api/src/routes/deviceMonitor.ts,
// UNKNOWN_DEVICE_USER) for access events with no mapped Hub identity. It is
// not a real Coworker.id, so it must never be linked (would 404).
const UNKNOWN_DEVICE_USER = 'unknown-device-user'
// Sentinel written by deviceMonitor.ts (GUEST_DEVICE_USER) for a guest's door
// QR scan. Not a Coworker.id either, so it is never linked.
const GUEST_DEVICE_USER = 'guest-device-user'

// Fixed set of window choices, mirroring the API's clamp of `window` to
// [1, 168] hours (apps/api/src/routes/accessActivity.ts).
const WINDOW_HOUR_OPTIONS: Array<{ hours: number; labelKey: string }> = [
  { hours: 1, labelKey: 'access.window1h' },
  { hours: 6, labelKey: 'access.window6h' },
  { hours: 12, labelKey: 'access.window12h' },
  { hours: 24, labelKey: 'access.window24h' },
  { hours: 48, labelKey: 'access.window48h' },
  { hours: 168, labelKey: 'access.window7d' },
]

/**
 * Access activity — "last seen" worklist (Task 5) plus an admin-only
 * "unknown badgers" reconciliation list (Task 6).
 *
 * NAMING/SAFETY CONSTRAINT (spec Decision D3): the ControlID device reports
 * a single portal with NO entry/exit direction, so it is physically
 * impossible to know who has left the building. This page shows "last
 * seen", NEVER presence or occupancy — do not add "present", "in the
 * building", or any headcount/total here. Someone last seen yesterday and
 * never having left through a monitored door would otherwise be
 * misrepresented as currently present, which is a safety-relevant falsehood
 * (e.g. an evacuation headcount).
 */
export function AccessActivityPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t, locale } = useTranslation()
  const isAdmin = useHasRole('admin')
  // Door actuation is admin + operator (Decision D2) — deliberately a
  // separate role check from `isAdmin` above, since the Unknown badgers tab
  // and the door button are gated differently.
  const canOpenDoor = useHasRole('admin', 'operator')

  const [activeTab, setActiveTab] = useState<AccessTab>('lastSeen')
  const [windowHours, setWindowHours] = useState(DEFAULT_WINDOW_HOURS)

  const [entries, setEntries] = useState<LastSeenEntry[]>([])
  const [identities, setIdentities] = useState<ActorIdentityMap>({})
  const [lastSeenLoading, setLastSeenLoading] = useState(true)
  const [lastSeenError, setLastSeenError] = useState<string | null>(null)

  const [unknownEntries, setUnknownEntries] = useState<UnknownBadger[]>([])
  const [unknownLoading, setUnknownLoading] = useState(true)
  const [unknownError, setUnknownError] = useState<string | null>(null)

  // Task 5 — remote door actuation. `actuatableReaderId` is `null` until we
  // know there is exactly one reader we're allowed to fire (or once we know
  // there isn't): the button must not render, even briefly, when there is
  // nothing to actuate. `firingRef` is the double-fire guard — it is set
  // synchronously in the click handler, before any `await`, so a second
  // click dispatched in the same tick (e.g. an eager double-click) is
  // rejected even though React state updates are asynchronous.
  const [actuatableReaderId, setActuatableReaderId] = useState<string | null>(null)
  const [doorState, setDoorState] = useState<DoorState>('idle')
  const firingRef = useRef(false)
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadLastSeen = useCallback(async () => {
    setLastSeenLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const data = await getLastSeen(token, windowHours)
      setEntries(data.entries)
      setIdentities(data.identities ?? {})
      setLastSeenError(null)
    } catch (err) {
      setLastSeenError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
    } finally {
      setLastSeenLoading(false)
    }
  }, [getAccessTokenSilently, windowHours])

  // Admin-only (matches the API: /api/access/unknown 403s for operators) —
  // skip the fetch entirely for a non-admin rather than firing a doomed
  // request.
  const loadUnknown = useCallback(async () => {
    if (!isAdmin) {
      setUnknownLoading(false)
      return
    }
    setUnknownLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const data = await getUnknownBadgers(token, windowHours)
      setUnknownEntries(data.entries)
      setUnknownError(null)
    } catch (err) {
      setUnknownError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
    } finally {
      setUnknownLoading(false)
    }
  }, [getAccessTokenSilently, isAdmin, windowHours])

  useEffect(() => {
    void loadLastSeen()
  }, [loadLastSeen])

  useEffect(() => {
    void loadUnknown()
  }, [loadUnknown])

  // Which reader can the button actuate? Exactly one → use it silently.
  // Empty, or the request failed (including a 403 for a role this hook
  // wasn't given — belt and suspenders alongside `canOpenDoor` below) → no
  // button at all, rather than one that always 409s. More than one reader
  // is out of scope for this task (no picker) — treated the same as empty,
  // since rendering a button wired to an arbitrarily-chosen reader would be
  // worse than rendering none.
  useEffect(() => {
    if (!canOpenDoor) {
      setActuatableReaderId(null)
      return
    }
    let cancelled = false
    async function loadReaders() {
      try {
        const token = await getAccessTokenSilently()
        const data = await listActuatableReaders(token)
        if (cancelled) return
        const [only] = data.readers
        setActuatableReaderId(data.readers.length === 1 && only ? only.id : null)
      } catch {
        if (!cancelled) setActuatableReaderId(null)
      }
    }
    void loadReaders()
    return () => {
      cancelled = true
    }
  }, [canOpenDoor, getAccessTokenSilently])

  // Clear any in-flight poll timer on unmount so it never fires (or leaks)
  // after the page is gone.
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current !== null) clearTimeout(pollTimeoutRef.current)
    }
  }, [])

  async function pollActuation(token: string, actuationId: string) {
    const deadline = Date.now() + DOOR_POLL_WINDOW_MS
    const tick = async () => {
      try {
        const actuation = await getActuation(token, actuationId)
        if (actuation.confirmedAt) {
          setDoorState('opened')
          firingRef.current = false
          return
        }
      } catch {
        // Transient poll failure — keep trying until the window elapses;
        // a single failed GET is not evidence the door didn't open.
      }
      if (Date.now() >= deadline) {
        // 🔴 Load-bearing, not decoration: this is the ONLY way an operator
        // learns the door did not open, because the device's HTTP response
        // ("status: allowed") is identical whether the relay fired or
        // silently no-opped. Never soften this into a success appearance.
        setDoorState('unconfirmed')
        firingRef.current = false
        return
      }
      pollTimeoutRef.current = setTimeout(() => void tick(), DOOR_POLL_INTERVAL_MS)
    }
    pollTimeoutRef.current = setTimeout(() => void tick(), DOOR_POLL_INTERVAL_MS)
  }

  function handleOpenDoor() {
    // Synchronous guard, before any `await`: rejects a second click fired
    // in the same tick as the first, which a state-based `disabled` check
    // alone cannot do (state updates are async).
    if (firingRef.current || !actuatableReaderId) return
    firingRef.current = true
    setDoorState('opening')
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const result = await openDoor(token, actuatableReaderId)
        if (result.deviceError) {
          // Never reached the device — no point polling for a confirmation
          // that was never requested.
          setDoorState('unconfirmed')
          firingRef.current = false
          return
        }
        await pollActuation(token, result.actuationId)
      } catch {
        setDoorState('unconfirmed')
        firingRef.current = false
      }
    })()
  }

  function formatLastSeen(ms: number): string {
    return new Date(ms).toLocaleString(locale)
  }

  const tabs = [
    { value: 'lastSeen', label: t('access.tabLastSeen') },
    // Device-wide data about people who may not be coworkers at all.
    ...(isAdmin ? [{ value: 'unknown', label: t('access.tabUnknown') }] : []),
  ]

  // Text tokens are semantic, not decorative: subtle while in flight,
  // success only once genuinely confirmed, and danger for "no
  // confirmation" — this must never read or look like a success state.
  const doorStateLabel: Record<Exclude<DoorState, 'idle'>, { key: string; className: string }> = {
    opening: { key: 'access.doorOpening', className: 'text-kumo-subtle' },
    opened: { key: 'access.doorOpened', className: 'text-kumo-success' },
    unconfirmed: { key: 'access.doorNoConfirmation', className: 'text-kumo-danger' },
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('access.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('access.title')}
        description={t('access.description')}
        tabs={tabs}
        defaultTab={activeTab}
        onValueChange={(v) => setActiveTab(v as AccessTab)}
      >
        {actuatableReaderId && (
          <div className="flex items-center gap-2">
            {doorState !== 'idle' && (
              <span className={`text-sm ${doorStateLabel[doorState].className}`}>
                {t(doorStateLabel[doorState].key)}
              </span>
            )}
            <Button
              variant="secondary"
              size="sm"
              icon={<DoorOpenIcon size={16} />}
              disabled={doorState === 'opening'}
              loading={doorState === 'opening'}
              onClick={handleOpenDoor}
            >
              {t('access.openDoor')}
            </Button>
          </div>
        )}
      </PageHeader>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select
          className="w-40"
          aria-label={t('access.windowLabel')}
          value={String(windowHours)}
          onValueChange={(v) => setWindowHours(Number(v))}
          items={WINDOW_HOUR_OPTIONS.map((opt) => ({
            value: String(opt.hours),
            label: t(opt.labelKey),
          }))}
        />
      </div>

      {activeTab === 'lastSeen' && (
        <>
          {lastSeenLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader size="lg" />
            </div>
          )}

          {!lastSeenLoading && lastSeenError && (
            <Banner variant="error">
              {t('common.errorPrefix')}{' '}
              {lastSeenError === 'GENERIC_ERROR' ? t('common.error') : lastSeenError}
            </Banner>
          )}

          {!lastSeenLoading && !lastSeenError && entries.length === 0 && (
            <Empty
              icon={<ClockCounterClockwiseIcon size={48} className="text-kumo-inactive" />}
              title={t('access.emptyLastSeen')}
            />
          )}

          {!lastSeenLoading && !lastSeenError && entries.length > 0 && (
            <LayerCard>
              <LayerCard.Primary className="p-0">
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <Table>
                    <Table.Header>
                      <Table.Row>
                        <Table.Head>{t('access.columnName')}</Table.Head>
                        <Table.Head>{t('access.columnCompany')}</Table.Head>
                        <Table.Head>{t('access.columnLastSeen')}</Table.Head>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {entries.map((e) => (
                        <Table.Row key={e.actorUid}>
                          {/* Fall back to the raw uid: unmapped actors have
                              no identity. Never link the device-user
                              sentinel — it is not a real Coworker.id. */}
                          <Table.Cell>
                            {e.actorUid === GUEST_DEVICE_USER ? (
                              t('access.guestDeviceUser')
                            ) : e.actorUid === UNKNOWN_DEVICE_USER ? (
                              e.actorUid
                            ) : (
                              <Link
                                to={`/coworkers/${e.actorUid}/edit`}
                                className="text-kumo-link underline"
                              >
                                {identities[e.actorUid]?.name ?? e.actorUid}
                              </Link>
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            {/* The label is the coworker's CURRENT company
                                (read-time D1 join via `identities`), but the
                                link target is `e.companyUid` — the company
                                frozen onto the event AT THE TIME OF ACCESS
                                (spec Decision "company-at-time-of-access").
                                These can differ for a coworker who has since
                                changed company; the access record's own
                                company is still the correct link target for
                                an access log entry. */}
                            {e.companyUid == null ? (
                              '—'
                            ) : (
                              <Link
                                to={`/companies/${e.companyUid}/edit`}
                                className="text-kumo-link underline"
                              >
                                {identities[e.actorUid]?.companyName ?? '—'}
                              </Link>
                            )}
                          </Table.Cell>
                          {/* "Last seen", never "present" — spec Decision D3. */}
                          <Table.Cell>{formatLastSeen(e.lastSeenAt)}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
              </LayerCard.Primary>
            </LayerCard>
          )}
        </>
      )}

      {activeTab === 'unknown' && isAdmin && (
        <>
          {unknownLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader size="lg" />
            </div>
          )}

          {!unknownLoading && unknownError && (
            <Banner variant="error">
              {t('common.errorPrefix')}{' '}
              {unknownError === 'GENERIC_ERROR' ? t('common.error') : unknownError}
            </Banner>
          )}

          {!unknownLoading && !unknownError && unknownEntries.length === 0 && (
            <Empty
              icon={<QuestionIcon size={48} className="text-kumo-inactive" />}
              title={t('access.emptyUnknown')}
            />
          )}

          {!unknownLoading && !unknownError && unknownEntries.length > 0 && (
            <LayerCard>
              <LayerCard.Primary className="p-0">
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <Table>
                    <Table.Header>
                      <Table.Row>
                        <Table.Head>{t('access.columnDeviceUserId')}</Table.Head>
                        <Table.Head>{t('access.columnHits')}</Table.Head>
                        <Table.Head>{t('access.columnLastSeen')}</Table.Head>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {unknownEntries.map((e) => (
                        <Table.Row key={e.deviceUserId}>
                          <Table.Cell>{e.deviceUserId}</Table.Cell>
                          <Table.Cell>{e.hits}</Table.Cell>
                          <Table.Cell>{formatLastSeen(e.lastSeenAt)}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
              </LayerCard.Primary>
            </LayerCard>
          )}
        </>
      )}
    </div>
  )
}
